import startCase from 'lodash/startCase'
import { Money, type MoneyJson } from '@klear/money'
import { Badge } from '@klear/ui/components/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@klear/ui/components/table'
import { trpc } from '@klear/ui/lib/trpc'

type AllianzCoverRequestsCardProps = {
  organizationId: number
  storedRating?: string | null
  storedLimit?: Money | MoneyJson | null
}

export function AllianzCoverRequestsCard({ organizationId, storedRating, storedLimit }: AllianzCoverRequestsCardProps) {
  const { data, error, isLoading } = trpc.credit.allianzActivity.useQuery({ organizationId })
  const stored = new Money(storedLimit ?? undefined)
  const live = data?.allianzCreditLimit ?? new Money()
  const differsFromStored =
    data != null && ((data.allianzRating ?? null) !== (storedRating ?? null) || !live.equals(stored))

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Allianz credit requests</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isLoading ? <p className="text-muted-foreground">Checking Allianz…</p> : null}
        {error ? <p className="text-destructive">{error.message}</p> : null}
        {data != null ? (
          <div>
            <p>
              Allianz now: grade {data.allianzRating ?? '—'}
              {data.allianzRatingChangedAt ? ` (since ${data.allianzRatingChangedAt})` : ''} · limit{' '}
              {live.isZero() ? '—' : live.toString()}
            </p>
            {differsFromStored ? (
              <p className="text-xs text-warning">
                Klear has grade {storedRating ?? '—'} · limit {stored.isZero() ? '—' : stored.toString()}. Refresh
                credit to update.
              </p>
            ) : null}
          </div>
        ) : null}
        {data?.requests.length === 0 ? (
          <p className="text-muted-foreground">Allianz has no credit requests for this buyer.</p>
        ) : null}
        {data != null && data.requests.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Requested</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Granted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.requests.map((request) => (
                <TableRow key={request.requestId}>
                  <TableCell className="whitespace-nowrap">
                    {request.createdAt ?? '—'}
                    {request.updatedAt != null && request.updatedAt !== request.createdAt ? (
                      <span className="block text-xs text-muted-foreground">updated {request.updatedAt}</span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={request.isOpen ? 'secondary' : 'outline'}>
                      {startCase(request.requestStatusCode ?? 'Unknown')}
                    </Badge>
                    {request.requestStatusMotiveCode != null && request.requestStatusMotiveCode !== 'Undefined' ? (
                      <span className="block pt-1 text-xs text-muted-foreground">
                        {startCase(request.requestStatusMotiveCode)}
                      </span>
                    ) : null}
                    <span className="block text-xs text-muted-foreground">{request.requestId}</span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {request.requestedAmount?.toString() ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {request.grantedAmount?.toString() ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  )
}
